
import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';

interface Product {
  id: number;
  name: string;
  category: string;
  description: string;
  image: string;
  price: string;
  rotation?: number;
  offsetX?: number;
  offsetY?: number;
  zIndex?: number;
}

export default function Home() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  const heroY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const illustrationY = useTransform(scrollYProgress, [0, 1], [0, -100]);
  // const doodleY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  // Mouse parallax effect
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX - window.innerWidth / 2) * 0.01,
        y: (e.clientY - window.innerHeight / 2) * 0.01
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Featured products with scattered layout
  const featuredProducts: Product[] = [
    {
      id: 1,
      name: 'Premium Strawberries',
      category: 'Frozen Fruits',
      description: 'IQF strawberries with perfect texture',
      image: 'https://readdy.ai/api/search-image?query=premium%20frozen%20strawberries%20in%20elegant%20packaging%2C%20bright%20red%20color%2C%20professional%20product%20photography%2C%20clean%20white%20background&width=300&height=200&seq=20&orientation=landscape',
      price: 'Export Quality',
      rotation: -4,
      offsetX: 10,
      offsetY: -5,
      zIndex: 3
    },
    {
      id: 2,
      name: 'Mixed Berry Blend',
      category: 'Frozen Fruits',
      description: 'Perfect blend of premium berries',
      image: 'https://readdy.ai/api/search-image?query=mixed%20frozen%20berries%20blend%20in%20premium%20packaging%2C%20colorful%20berries%2C%20professional%20food%20photography%2C%20modern%20design&width=300&height=200&seq=21&orientation=landscape',
      price: 'Bulk Available',
      rotation: 6,
      offsetX: -15,
      offsetY: 10,
      zIndex: 2
    },
    {
      id: 3,
      name: 'Broccoli Florets',
      category: 'Frozen Vegetables',
      description: 'Fresh green broccoli florets',
      image: 'https://readdy.ai/api/search-image?query=frozen%20broccoli%20florets%20in%20professional%20packaging%2C%20bright%20green%20color%2C%20healthy%20vegetables%2C%20premium%20quality%20display&width=300&height=200&seq=22&orientation=landscape',
      price: 'Export Ready',
      rotation: -2,
      offsetX: 8,
      offsetY: -8,
      zIndex: 4
    },
    {
      id: 4,
      name: 'Freeze-Dried Mango',
      category: 'Freeze-Dried',
      description: 'Intense flavor concentrated mango',
      image: 'https://readdy.ai/api/search-image?query=freeze-dried%20mango%20pieces%20in%20elegant%20container%2C%20golden%20yellow%20color%2C%20premium%20dried%20fruit%2C%20professional%20display&width=300&height=200&seq=23&orientation=landscape',
      price: 'Premium Grade',
      rotation: 8,
      offsetX: -12,
      offsetY: 5,
      zIndex: 1
    },
    {
      id: 5,
      name: 'Apple Puree',
      category: 'Purees',
      description: 'Natural apple puree concentrate',
      image: 'https://readdy.ai/api/search-image?query=apple%20puree%20concentrate%20in%20professional%20container%2C%20natural%20golden%20color%2C%20food%20processing%2C%20premium%20quality&width=300&height=200&seq=24&orientation=landscape',
      price: 'Industrial Grade',
      rotation: -6,
      offsetX: 20,
      offsetY: -3,
      zIndex: 5
    },
    {
      id: 6,
      name: 'Pomegranate Juice',
      category: 'Juices',
      description: 'Pure pomegranate concentrate',
      image: 'https://readdy.ai/api/search-image?query=pomegranate%20juice%20concentrate%20in%20glass%20container%2C%20deep%20red%20color%2C%20premium%20beverage%2C%20professional%20photography&width=300&height=200&seq=25&orientation=landscape',
      price: 'Export Quality',
      rotation: 3,
      offsetX: -8,
      offsetY: 12,
      zIndex: 2
    }
  ];

  const stats = [
    { number: '50+', label: 'Countries Served', icon: 'ri-global-line' },
    { number: '25+', label: 'Years Experience', icon: 'ri-time-line' },
    { number: '1000+', label: 'Product Lines', icon: 'ri-product-hunt-line' },
    { number: '99.9%', label: 'Quality Rate', icon: 'ri-award-line' }
  ];

  // Floating fruit illustrations data
  const floatingElements = [
    { icon: '🍓', x: 15, y: 20, delay: 0, scale: 1.2 },
    { icon: '🥦', x: 85, y: 30, delay: 0.5, scale: 1.0 },
    { icon: '🍋', x: 10, y: 70, delay: 1, scale: 1.1 },
    { icon: '🥕', x: 90, y: 80, delay: 1.5, scale: 0.9 },
    { icon: '🍊', x: 75, y: 15, delay: 2, scale: 1.3 },
    { icon: '🥬', x: 20, y: 85, delay: 2.5, scale: 1.0 }
  ];

  // Card animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: -100, 
      rotate: Math.random() * 60 - 30,
      scale: 0.8
    },
    visible: (custom: any) => ({
      opacity: 1,
      y: custom.offsetY,
      x: custom.offsetX,
      rotate: custom.rotation,
      scale: 1,
      zIndex: custom.zIndex,
      transition: {
        duration: 0.8,
        delay: custom.delay,
        ease: [0.25, 0.46, 0.45, 0.94],
        type: "spring",
        damping: 15,
        stiffness: 100
      }
    }),
    hover: {
      rotate: 0,
      y: -15,
      scale: 1.05,
      zIndex: 10,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  return (
    <div ref={containerRef} className="min-h-screen bg-white overflow-hidden">
      <Header />
      
      {/* Hero Section with Floating Elements */}
      <motion.section 
        ref={heroRef}
        style={{ y: heroY }}
        className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20"
      >
        {/* Background Image - Same as About page */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=Hand-drawn%20fruits%20and%20vegetables%20doodles%2C%20sketch%20style%20illustrations%2C%20organic%20produce%20drawings%2C%20colorful%20food%20sketches%2C%20healthy%20eating%20doodles%2C%20minimalist%20line%20art%2C%20white%20background%20with%20scattered%20fruit%20vegetable%20illustrations&width=1920&height=1080&seq=abouthero&orientation=landscape)`
          }}
        >
          <div className="absolute inset-0 bg-white/85"></div>
        </div>

        {/* Floating Fruits & Vegetables */}
        {floatingElements.map((element, index) => (
          <motion.div
            key={index}
            className="absolute pointer-events-none"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              x: mousePosition.x * (index + 1) * 2,
              y: mousePosition.y * (index + 1) * 2,
            }}
            animate={{
              y: [0, -20, 0],
              rotate: [0, 5, -5, 0],
            }}
            transition={{
              duration: 4 + index,
              repeat: Infinity,
              delay: element.delay,
              ease: "easeInOut"
            }}
          >
            <motion.div
              style={{ scale: element.scale, y: illustrationY }}
              className="text-6xl md:text-8xl filter drop-shadow-lg"
              whileHover={{ scale: element.scale * 1.2 }}
            >
              {element.icon}
            </motion.div>
          </motion.div>
        ))}

        {/* Hero Content */}
        <div className="container mx-auto px-4 text-center z-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="max-w-5xl mx-auto"
          >
            <motion.span
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="text-primary-600 font-semibold text-lg uppercase tracking-wider mb-6 block"
            >
              Premium Export Quality
            </motion.span>
            
            <motion.h1
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 1.2, ease: "easeOut" }}
              className="text-5xl md:text-7xl lg:text-8xl font-black text-gray-900 mb-8 leading-tight"
              style={{
                fontFamily: "'Poppins', sans-serif",
                background: 'linear-gradient(135deg, #1e40af 0%, #10b981 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}
            >
              Frozen Foods
              <br />
              <span className="text-gray-800">Worldwide</span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.8 }}
              className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto"
            >
              Premium frozen fruits, vegetables, and processed products exported to 50+ countries. 
              Quality guaranteed, freshness preserved.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 2.2 }}
              className="flex flex-col sm:flex-row gap-6 justify-center"
            >
              <Button 
                size="lg"
                onClick={() => navigate('/products')}
                className="bg-gradient-to-r from-primary-600 to-green-600 hover:from-primary-700 hover:to-green-700 text-white shadow-xl hover:shadow-2xl"
              >
                Explore Products
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/contact')}
                className="border-2 border-gray-800 text-gray-800 hover:bg-gray-800 hover:text-white"
              >
                Get Export Quote
              </Button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 3 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-500 flex flex-col items-center"
        >
          <span className="text-sm mb-2">Discover More</span>
          <motion.i
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="ri-arrow-down-line text-2xl"
          ></motion.i>
        </motion.div>
      </motion.section>

      {/* Stats Section */}
      <section className="py-20 bg-white relative">
        {/* Background Doodle */}
        <motion.div
          className="absolute top-10 right-10 w-24 h-24 opacity-10"
          animate={{ rotate: 360 }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full text-blue-400">
            <motion.path
              d="M50,10 L60,40 L90,40 L70,60 L80,90 L50,70 L20,90 L30,60 L10,40 L40,40 Z"
              fill="currentColor"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 3 }}
            />
          </svg>
        </motion.div>

        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
                className="text-center"
              >
                <div className="w-20 h-20 bg-gradient-to-r from-primary-100 to-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${stat.icon} text-3xl text-primary-600`}></i>
                </div>
                <div className="text-4xl md:text-5xl font-black text-gray-900 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-600 font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured Products - Scattered Playing Cards */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-blue-50 relative overflow-hidden" data-product-shop>
        {/* Background Elements */}
        <motion.div
          className="absolute top-20 left-10 w-16 h-16 opacity-15"
          animate={{ rotate: [0, 180, 360] }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full text-green-400">
            <motion.polygon
              points="50,10 90,90 10,90"
              fill="currentColor"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 1, delay: 4 }}
            />
          </svg>
        </motion.div>

        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary-600 font-semibold text-sm uppercase tracking-wider mb-4 block">
              Featured Products
            </span>
            <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Premium Selection
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Discover our most popular export products, loved by international buyers worldwide
            </p>
          </motion.div>

          {/* Scattered Product Cards */}
          <div className="relative max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.id}
                  custom={{
                    rotation: product.rotation,
                    offsetX: product.offsetX,
                    offsetY: product.offsetY,
                    zIndex: product.zIndex,
                    delay: index * 0.15
                  }}
                  variants={cardVariants}
                  initial="hidden"
                  whileInView="visible"
                  whileHover="hover"
                  viewport={{ once: true, margin: "-50px" }}
                  className="relative cursor-pointer group"
                  style={{
                    transformOrigin: 'center center',
                  }}
                  onClick={() => navigate(`/products/${product.id}`)}
                >
                  {/* Card Shadow */}
                  <motion.div
                    className="absolute inset-0 bg-black/15 rounded-2xl blur-lg"
                    style={{
                      transform: `translate(${(product.offsetX || 0) * 0.3}px, ${((product.offsetY || 0) * 0.3) + 12}px)`
                    }}
                  />
                  
                  {/* Main Card */}
                  <div className="relative bg-white rounded-2xl shadow-xl group-hover:shadow-2xl transition-all duration-500 overflow-hidden border-2 border-gray-100 group-hover:border-primary-200">
                    {/* Product Image */}
                    <div className="relative">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-48 object-cover object-top group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="bg-gradient-to-r from-primary-600 to-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                          {product.category}
                        </span>
                      </div>
                      
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>

                    {/* Product Info */}
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {product.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-primary-600 font-bold text-lg">
                          {product.price}
                        </span>
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-all duration-300 cursor-pointer"
                        >
                          <i className="ri-arrow-right-line"></i>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* View All Products CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-center mt-16"
          >
            <Button 
              size="lg"
              onClick={() => navigate('/products')}
              className="bg-gradient-to-r from-gray-900 to-gray-700 hover:from-gray-800 hover:to-gray-600 text-white shadow-xl"
            >
              View All Products
              <i className="ri-grid-line ml-2"></i>
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 via-blue-600 to-green-600 relative overflow-hidden">
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            className="absolute top-0 left-0 w-full h-full"
          >
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  scale: [1, 1.5, 1],
                  opacity: [0.3, 1, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </motion.div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6" style={{ fontFamily: "'Poppins', sans-serif" }}>
              Ready to Export Globally?
            </h2>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
              Join thousands of satisfied international buyers. Get a custom quote 
              for your frozen food requirements today.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/contact')}
                className="bg-white text-primary-600 hover:bg-gray-100 shadow-xl"
              >
                Get Export Quote
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate('/products')}
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600"
              >
                View Products
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
